import { useEffect, useMemo, useState } from "react";
import { Button, Card, FormLayout, Layout, Page, Text, TextField } from "@shopify/polaris";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import { clearAdminToken, getAdminToken, setAdminToken } from "./adminAuth";
import "./admin-console.css";

export default function AdminPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setToken(getAdminToken());
  }, []);

  const handleLogout = () => {
    clearAdminToken();
    setToken(null);
    setError(null);
    navigate("/pf-console");
  };

  const handleLogin = async () => {
    setError(null);

    try {
      const response = await agent.Admin.login(email, password);
      setAdminToken(response.token);
      setToken(response.token);
      navigate("/pf-console/dashboard");
    } catch {
      setError("Invalid admin credentials.");
    }
  };

  const navigationItems = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", path: "/pf-console/dashboard", active: location.pathname.startsWith("/pf-console/dashboard") },
      { key: "activity", label: "Activity", path: "/pf-console/activity", active: location.pathname.startsWith("/pf-console/activity") },
      { key: "stores", label: "Stores", path: "/pf-console/stores", active: location.pathname.startsWith("/pf-console/stores") || location.pathname.startsWith("/pf-console/legacy-stores") },
      { key: "sync-log", label: "Sync logs", path: "/pf-console/sync-logs", active: location.pathname.startsWith("/pf-console/sync-logs") },
      { key: "error-log", label: "Error logs", path: "/pf-console/error-logs", active: location.pathname.startsWith("/pf-console/error-logs") },
      { key: "webhook-log", label: "Webhook logs", path: "/pf-console/webhook-logs", active: location.pathname.startsWith("/pf-console/webhook-logs") },
      { key: "privacy", label: "Privacy requests", path: "/pf-console/privacy-requests", active: location.pathname.startsWith("/pf-console/privacy-requests") },
      { key: "data-feed", label: "Data Feed", path: "/pf-console/data-feed", active: location.pathname.startsWith("/pf-console/data-feed") },
      { key: "fitment-import-log", label: "Fitment import logs", path: "/pf-console/fitment-import-logs", active: location.pathname.startsWith("/pf-console/fitment-import-logs") },
      { key: "turn14", label: "Turn14", path: "/pf-console/turn14", active: location.pathname.startsWith("/pf-console/turn14") },
      { key: "system", label: "System", path: "/pf-console/system", active: location.pathname.startsWith("/pf-console/system") },
    ],
    [location.pathname]
  );

  if (!token) {
    return (
      <Page fullWidth title="PartFits Admin Console">
        <Layout>
          <Layout.Section>
            <Card>
              <FormLayout>
                <Text as="h2" variant="headingMd">Admin login</Text>
                <TextField label="Email" value={email} onChange={setEmail} autoComplete="email" />
                <TextField label="Password" value={password} onChange={setPassword} type="password" autoComplete="current-password" />
                <Button variant="primary" onClick={handleLogin}>Sign in</Button>
                {error ? <Text as="p" tone="critical">{error}</Text> : null}
              </FormLayout>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      fullWidth
      title="PartFits Admin Console"
      subtitle="Manage stores, monitor logs, and configure console settings"
      secondaryActions={[{ content: "Logout", onAction: handleLogout }]}
    >
      <div className="admin-console-layout">
        <Card>
          <div style={{ display: "grid", gap: "0.35rem" }}>
            {navigationItems.map((item) => (
              <Button
                key={item.key}
                fullWidth
                textAlign="left"
                variant={item.active ? "primary" : "tertiary"}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </Card>
        <div className="admin-console-content">
          {location.pathname === "/pf-console" ? (
            <Navigate replace to="/pf-console/dashboard" />
          ) : (
            <Outlet context={{ token, onUnauthorized: handleLogout }} />
          )}
        </div>
      </div>
    </Page>
  );
}
